import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useSettings } from '../../../contexts/SettingsContext';
import { DashboardOverview, formatEuro, formatMonth } from './types';

export const AXIS = { fontSize: 12, fill: '#6b7280' };
export const GRID = '#e5e7eb';
const PAYMENT_COLORS: Record<string, string> = { pago: '#16a34a', pendente: '#f59e0b', atrasado: '#dc2626' };
const QUOTE_COLORS: Record<string, string> = {
  draft: '#9ca3af',
  sent: '#3b82f6',
  accepted: '#8b5cf6',
  converted: '#16a34a',
  declined: '#dc2626',
  expired: '#f97316',
};

export const ChartCard: React.FC<{ title: string; empty: boolean; children: React.ReactElement }> = ({ title, empty, children }) => {
  const { t } = useTranslation();
  return (
    <div className="card">
      <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 md:mb-4">{title}</h3>
      {empty ? (
        <div className="h-64 flex items-center justify-center text-sm text-gray-500">{t('dashboardHome.charts.empty')}</div>
      ) : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            {children}
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

interface DashboardChartsProps {
  charts: DashboardOverview['charts'];
}

const DashboardCharts: React.FC<DashboardChartsProps> = ({ charts }) => {
  const { t, i18n } = useTranslation();
  const { settings } = useSettings();
  const lang = i18n.language;
  const primary = settings?.appearance?.primaryColor || '#16a34a';
  const secondary = settings?.appearance?.secondaryColor || '#dc2626';

  const revenue = charts.revenue.map((row) => ({ ...row, label: formatMonth(row.month, lang) }));
  const intake = charts.intake.map((row) => ({ ...row, label: formatMonth(row.month, lang) }));
  const quoteFunnel = charts.quoteFunnel.map((row) => ({
    ...row,
    label: t(`dashboardHome.charts.quoteStatus.${row.status}`),
  }));
  const paymentStatus = charts.paymentStatus
    .filter((row) => row.count > 0)
    .map((row) => ({ ...row, label: t(`dashboardHome.charts.paymentStatusLabels.${row.status}`) }));

  const euro = (value: number) => formatEuro(value, lang);

  return (
    <div className="mt-6 md:mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
      <ChartCard title={t('dashboardHome.charts.revenue')} empty={revenue.every((r) => !r.paid && !r.open)}>
        <BarChart data={revenue} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
          <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={false} />
          <YAxis tick={AXIS} tickLine={false} axisLine={false} width={64} tickFormatter={(v) => formatEuro(v, lang, true)} />
          <Tooltip formatter={(value) => euro(Number(value))} cursor={{ fill: '#f3f4f6' }} />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="paid" name={t('dashboardHome.charts.paid')} stackId="r" fill={primary} />
          <Bar dataKey="open" name={t('dashboardHome.charts.open')} stackId="r" fill="#f59e0b" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ChartCard>

      <ChartCard
        title={t('dashboardHome.charts.intake')}
        empty={intake.every((r) => !r.nif && !r.visa && !r.residence)}
      >
        <LineChart data={intake} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
          <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={false} />
          <YAxis tick={AXIS} tickLine={false} axisLine={false} width={32} allowDecimals={false} />
          <Tooltip />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="nif" name={t('dashboardHome.charts.nif')} stroke={primary} strokeWidth={2} dot={{ r: 3 }} />
          <Line type="monotone" dataKey="visa" name={t('dashboardHome.charts.visa')} stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} />
          <Line type="monotone" dataKey="residence" name={t('dashboardHome.charts.residence')} stroke={secondary} strokeWidth={2} dot={{ r: 3 }} />
        </LineChart>
      </ChartCard>

      <ChartCard title={t('dashboardHome.charts.quoteFunnel')} empty={quoteFunnel.every((r) => !r.count)}>
        <BarChart data={quoteFunnel} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
          <XAxis type="number" tick={AXIS} tickLine={false} axisLine={false} allowDecimals={false} />
          <YAxis type="category" dataKey="label" tick={AXIS} tickLine={false} axisLine={false} width={88} />
          <Tooltip cursor={{ fill: '#f3f4f6' }} />
          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
            {quoteFunnel.map((row) => (
              <Cell key={row.status} fill={QUOTE_COLORS[row.status] || '#9ca3af'} />
            ))}
          </Bar>
        </BarChart>
      </ChartCard>

      <ChartCard title={t('dashboardHome.charts.paymentStatus')} empty={paymentStatus.length === 0}>
        <PieChart>
          <Pie data={paymentStatus} dataKey="total" nameKey="label" innerRadius="55%" outerRadius="80%" paddingAngle={2}>
            {paymentStatus.map((row) => (
              <Cell key={row.status} fill={PAYMENT_COLORS[row.status]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value, _name, item: any) => `${euro(Number(value))} · ${item?.payload?.count ?? 0}x`}
          />
          <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
        </PieChart>
      </ChartCard>
    </div>
  );
};

export default DashboardCharts;
